// Per-person excursion rates tailored to the party on the booking.
//
// iVector prices excursions per adult / child / infant. The shop and checkout used to label the
// adult rate "/person", which misreads for parties with children (children are typically 50%).
// rateBreakdown() turns { adultPrice, childPrice, infantPrice } plus the party counts into:
//   headline        the first group's price (always the adults' group)
//   headlineSuffix  "/person" when everyone pays the same, else "/adult" (or "/adult or child" when
//                   adults and children share a price but infants differ)
//   others          the remaining groups as { label, price, free } — only for passenger types the
//                   party actually has; types with the same price merge into one line
//                   ("Children & infants £17.70"); a zero price reads as free ("Infants free").
// Returns null when there is no adult rate (per-booking pricing): callers show the party total.

const PLURAL = { adult: "adults", child: "children", infant: "infants" };
const SINGULAR = { adult: "adult", child: "child", infant: "infant" };

const price = (v) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);
const capitalise = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const rateBreakdown = (rates, party) => {
    const adult = price(rates?.adultPrice);
    if (!adult) return null;

    const groups = [{ types: ["adult"], price: adult }];
    const add = (type, value) => {
        const p = price(value);
        if (p == null) return; // rate unknown for this type: say nothing rather than guess
        const same = groups.find((g) => g.price === p);
        if (same) same.types.push(type); else groups.push({ types: [type], price: p });
    };
    if (party?.children > 0) add("child", rates?.childPrice);
    if (party?.infants > 0) add("infant", rates?.infantPrice);

    // "/person" when everyone in the party pays the same — including an adults-only party, where
    // "per adult" would only raise the question of what a child costs.
    const typesInParty = 1 + (party?.children > 0 ? 1 : 0) + (party?.infants > 0 ? 1 : 0);
    const everyoneSame = groups.length === 1 && groups[0].types.length === typesInParty;

    const label = (types) => types.map((t, i) => (i === 0 ? capitalise(PLURAL[t]) : PLURAL[t])).join(" & ");
    return {
        headline: groups[0].price,
        headlineSuffix: everyoneSame ? "/person" : `/${groups[0].types.map((t) => SINGULAR[t]).join(" or ")}`,
        others: groups.slice(1).map((g) => ({ label: label(g.types), price: g.price, free: g.price === 0 })),
    };
};

/** Party counts from a list of guests carrying iVector passenger types (Adult / Child / Infant). */
export const partyOf = (guests) => (guests || []).reduce((acc, g) => {
    const t = (g?.type || "").toLowerCase();
    if (t === "child") acc.children += 1;
    else if (t === "infant") acc.infants += 1;
    else acc.adults += 1;
    return acc;
}, { adults: 0, children: 0, infants: 0 });
